import { promiseTimeout } from "@vueuse/core";

/**
 * Loads the custom elements config from the script origin.
 *
 * @returns {Promise<Object>} The config
 * @throws {Error} If the config is not found
 */
export async function loadConfig() {
  try {
    const origin = window.sparkleEmbedApiHost ?? (await detectScriptOrigin());

    return await fetchConfig(origin);
  } catch (cause) {
    throw new Error("Failed to load config", { cause });
  }
}

/**
 * Detects the origin of the script that loads the custom elements.
 *
 * @private
 * @returns {Promise<string>} The origin of the script
 * @throws {Error} If the script origin is not found
 */
async function detectScriptOrigin() {
  const scriptElement = await findCustomElementsScriptElement();

  if (scriptElement) {
    return new URL(scriptElement.src).origin;
  }

  throw new Error("Custom elements script origin not found");
}

/**
 * Finds the script element that loads the custom elements.
 *
 * @private
 * @returns {Promise<HTMLScriptElement | undefined>} The script element
 */
async function findCustomElementsScriptElement() {
  const delay = 50;
  const maxAttempts = 10;
  const selectors = ["entrypoints", "assets"].map((prefix) => `script[src*='${prefix}/custom_elements']`).join(", ");

  let attempts = 0;

  while (attempts < maxAttempts) {
    attempts++;

    const scriptElement = document.currentScript ?? import.meta.currentScript ?? document.querySelector(selectors);

    if (scriptElement) {
      return scriptElement;
    }

    // Don't block the loop if we've reached the maximum number of attempts.
    if (attempts < maxAttempts) {
      await promiseTimeout(delay * attempts);
    }
  }

  return undefined;
}

/**
 * Fetches the custom elements config from the script origin.
 * The request will timeout after 20 seconds.
 *
 * @private
 * @returns {Promise<Object>} The config
 * @throws {Error} If the response is not ok
 */
async function fetchConfig(origin) {
  const timeout = 20_000;
  const url = buildConfigUrl(origin);
  const signal = AbortSignal.timeout(timeout);
  const credentials = "include";
  const response = await fetch(url.href, { signal, credentials });

  if (response.ok) {
    return await response.json();
  }

  throw new Error("Failed to fetch config", { cause: response.statusText });
}

/**
 * Builds the URL for the config endpoint based on the origin of the script that loads the custom elements.
 *
 * @private
 * @param {string} origin - The origin of the script
 * @returns {URL} The URL for the config endpoint
 */
function buildConfigUrl(origin) {
  const url = new URL(origin);

  url.pathname = "/sp_components/configuration.json";

  return url;
}
